import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';

import { AuthService } from '../../services/auth.service';
import { StudentService } from '../../services/student.service';
import { AccreditationService } from '../../services/accreditation.service';
import { Student, StatusHistoryEntry } from '../../interfaces/student.interfaces';
import { environment } from '../../../environments/environment';
import { FolioPillComponent } from '../ui/folio-pill/folio-pill.component';

@Component({
  selector: 'app-student-profile',
  standalone: true,
  imports: [CommonModule, FormsModule, FolioPillComponent],
  templateUrl: './student-profile.html',
  styleUrl: './student-profile.scss',
})
export class StudentProfile implements OnInit, OnDestroy {
  private authService = inject(AuthService);
  private studentService = inject(StudentService);
  private accreditationService = inject(AccreditationService);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);

  student: Student | null = null;
  accreditations: any[] = [];
  isLoading = true;

  // Pestañas de Acreditaciones / Actividades / Todas
  accActiveTab: 'acreditaciones' | 'actividades' | 'todas' = 'todas';

  // Copia de folio al portapapeles (sin iconos, con sombreado en hover)
  folioCopied = false;
  private copyTimeout: any = null;

  // Estados de edición independientes por sección
  isEditingPersonal = false;
  isSavingPersonal = false;
  backupPersonal = { phone: '', mail: '' };

  isEditingBio = false;
  isSavingBio = false;
  backupBio = '';

  isEditingSocials = false;
  isSavingSocials = false;
  backupSocials = {
    facebook: '',
    instagram: '',
    tiktok: '',
    x: '',
    youtube: '',
    linkedin: '',
  };

  // Mensajes de retroalimentación
  feedbackMessage = '';
  feedbackError = '';
  private feedbackTimeout: any = null;

  legalDocs: Record<string, { title: string; url: string }> = {
    reglamento: {
      title: 'Reglamento General Interno',
      url: '',
    },
    privacidad: {
      title: 'Aviso de Privacidad Integral',
      url: '',
    },
    etica: {
      title: 'Código de Ética y Conducta',
      url: '',
    },
    incognito: {
      title: 'Permiso de Difusión Institucional',
      url: '',
    },
    consentimiento: {
      title: 'Consentimiento Voluntario Expreso',
      url: '',
    },
  };

  ngOnInit(): void {
    this.authService.user$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((user) => {
        if (!user?.id) {
          this.isLoading = false;
          this.cdr.detectChanges();
          return;
        }

        this.studentService
          .getStudentByAuthId(user.id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: (student) => {
              this.student = this.ensureStudentDefaults(student);
              this.loadAccreditations(this.student);
              this.isLoading = false;
              this.cdr.detectChanges();
            },
            error: (err) => {
              console.error('ERROR OBTENIENDO ESTUDIANTE EN PERFIL:', err);
              this.isLoading = false;
              this.cdr.detectChanges();
            },
          });
      });
  }

  ngOnDestroy(): void {
    if (this.copyTimeout) {
      clearTimeout(this.copyTimeout);
    }
    if (this.feedbackTimeout) {
      clearTimeout(this.feedbackTimeout);
    }
  }

  private ensureStudentDefaults(student: Student): Student {
    if (!student.socialMedia) {
      student.socialMedia = {
        facebook: '',
        instagram: '',
        tiktok: '',
        x: '',
        youtube: '',
        linkedin: '',
      };
    }
    if (!student.assignedActivities) {
      student.assignedActivities = [];
    }
    if (!student.periods) {
      student.periods = [];
    }
    return student;
  }

  private loadAccreditations(student: Student): void {
    if (student.accreditations && student.accreditations.length > 0) {
      this.accreditations = student.accreditations;
    }
    const studentId = (student as any)?._id || student?.id;
    if (studentId) {
      this.accreditationService
        .getStudentAccreditations(studentId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (res: any) => {
            const list = res?.data?.documents || res?.data || res || [];
            if (Array.isArray(list) && list.length > 0) {
              this.accreditations = list;
            }
            this.cdr.detectChanges();
          },
          error: () => {
            this.accreditations = student.accreditations || [];
            this.cdr.detectChanges();
          },
        });
    }
  }

  // --- MÉTRICAS DEL ENCABEZADO Y BITÁCORA DE ESTATUS ---
  get opportunitiesCount(): number {
    return this.accreditations?.length || 0;
  }

  get activitiesCount(): number {
    return this.student?.assignedActivities?.length || 0;
  }

  get totalStatsCount(): number {
    return this.opportunitiesCount + this.activitiesCount;
  }

  get lastStatusChange(): StatusHistoryEntry | null {
    const history = this.student?.statusHistory;
    if (!history || history.length === 0) {
      return null;
    }
    return [...history].sort((a, b) => {
      const da = a.date ? new Date(a.date).getTime() : 0;
      const db = b.date ? new Date(b.date).getTime() : 0;
      return db - da;
    })[0];
  }

  getFriendlyReason(reason?: string): string {
    if (!reason) return 'Decisión administrativa institucional';
    const map: Record<string, string> = {
      initial_signup: 'Registro inicial completado',
      admin_approval: 'Aprobación oficial de expediente',
      admin_rejection: 'Rechazo con observaciones de Coordinación',
      hours_completed: 'Conclusión satisfactoria de 480 horas de servicio',
      administrative_decision: 'Baja administrativa institucional',
      re_registration: 'Re-registro y corrección de expediente',
    };
    return map[reason] || reason;
  }

  // --- COPIAR FOLIO AL CLIC (SIN ICONOS, CON SOMBREADO EN HOVER) ---
  copyFolio(folio?: string): void {
    if (!folio) return;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard
        .writeText(folio)
        .then(() => this.triggerCopiedToast())
        .catch(() => this.fallbackCopy(folio));
    } else {
      this.fallbackCopy(folio);
    }
  }

  private triggerCopiedToast(): void {
    this.folioCopied = true;
    if (this.copyTimeout) {
      clearTimeout(this.copyTimeout);
    }
    this.copyTimeout = setTimeout(() => {
      this.folioCopied = false;
      this.cdr.detectChanges();
    }, 2200);
    this.cdr.detectChanges();
  }

  private fallbackCopy(text: string): void {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      this.triggerCopiedToast();
    } catch {
      // Ignorar fallo de fallback
    }
  }

  // --- GESTIÓN DE PESTAÑAS ---
  setAccTab(tab: 'acreditaciones' | 'actividades' | 'todas'): void {
    this.accActiveTab = tab;
  }

  // --- SECCIÓN: INFORMACIÓN PERSONAL (EDITABLE: TELÉFONO Y CORREO PERSONAL) ---
  startEditPersonal(): void {
    if (!this.student) return;
    this.backupPersonal = {
      phone: this.student.phone || '',
      mail: this.student.mail || '',
    };
    this.isEditingPersonal = true;
  }

  cancelEditPersonal(): void {
    if (this.student) {
      this.student.phone = this.backupPersonal.phone;
      this.student.mail = this.backupPersonal.mail;
    }
    this.isEditingPersonal = false;
  }

  savePersonal(): void {
    if (!this.student) return;
    this.isSavingPersonal = true;
    this.clearFeedback();

    this.studentService
      .updateMyProfile({
        telefono: (this.student.phone || '').trim(),
        correoPersonal: (this.student.mail || '').trim(),
      })
      .subscribe({
        next: (updated) => {
          this.isSavingPersonal = false;
          this.isEditingPersonal = false;
          this.student = this.ensureStudentDefaults(updated);
          this.showFeedback('Datos de contacto actualizados correctamente.');
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.isSavingPersonal = false;
          this.showError(
            err?.error?.message || 'No fue posible actualizar tus datos de contacto.',
          );
          this.cdr.detectChanges();
        },
      });
  }

  // --- SECCIÓN: SEMBLANZA / BIO PERSONAL ---
  startEditBio(): void {
    if (!this.student) return;
    this.backupBio = this.student.bioPersonal || '';
    this.isEditingBio = true;
  }

  cancelEditBio(): void {
    if (this.student) {
      this.student.bioPersonal = this.backupBio;
    }
    this.isEditingBio = false;
  }

  saveBio(): void {
    if (!this.student) return;
    this.isSavingBio = true;
    this.clearFeedback();

    this.studentService
      .updateMyProfile({
        bioPersonal: (this.student.bioPersonal || '').trim(),
      })
      .subscribe({
        next: (updated) => {
          this.isSavingBio = false;
          this.isEditingBio = false;
          this.student = this.ensureStudentDefaults(updated);
          this.showFeedback('Semblanza personal actualizada correctamente.');
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.isSavingBio = false;
          this.showError(
            err?.error?.message || 'No fue posible actualizar tu semblanza.',
          );
          this.cdr.detectChanges();
        },
      });
  }

  // --- SECCIÓN: REDES SOCIALES ---
  startEditSocials(): void {
    if (!this.student) return;
    const s = this.student.socialMedia || ({} as any);
    this.backupSocials = {
      facebook: s.facebook || '',
      instagram: s.instagram || '',
      tiktok: s.tiktok || '',
      x: s.x || '',
      youtube: s.youtube || '',
      linkedin: s.linkedin || '',
    };
    this.isEditingSocials = true;
  }

  cancelEditSocials(): void {
    if (this.student) {
      if (!this.student.socialMedia) {
        this.student.socialMedia = {} as any;
      }
      Object.assign(this.student.socialMedia, this.backupSocials);
    }
    this.isEditingSocials = false;
  }

  saveSocials(): void {
    if (!this.student) return;
    this.isSavingSocials = true;
    this.clearFeedback();

    const sm = this.student.socialMedia || ({} as any);
    this.studentService
      .updateMyProfile({
        facebookUsuario: (sm.facebook || '').trim(),
        instagramUsuario: (sm.instagram || '').trim(),
        tiktokUsuario: (sm.tiktok || '').trim(),
        xUsuario: (sm.x || '').trim(),
        youtubeUsuario: (sm.youtube || '').trim(),
        linkedinUsuario: (sm.linkedin || '').trim(),
      })
      .subscribe({
        next: (updated) => {
          this.isSavingSocials = false;
          this.isEditingSocials = false;
          this.student = this.ensureStudentDefaults(updated);
          this.showFeedback('Redes sociales actualizadas correctamente.');
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.isSavingSocials = false;
          this.showError(
            err?.error?.message || 'No fue posible actualizar tus redes sociales.',
          );
          this.cdr.detectChanges();
        },
      });
  }

  // --- FEEDBACK HELPERS ---
  private showFeedback(msg: string): void {
    this.feedbackMessage = msg;
    if (this.feedbackTimeout) {
      clearTimeout(this.feedbackTimeout);
    }
    this.feedbackTimeout = setTimeout(() => {
      this.feedbackMessage = '';
      this.cdr.detectChanges();
    }, 4000);
  }

  private showError(msg: string): void {
    this.feedbackError = msg;
    if (this.feedbackTimeout) {
      clearTimeout(this.feedbackTimeout);
    }
    this.feedbackTimeout = setTimeout(() => {
      this.feedbackError = '';
      this.cdr.detectChanges();
    }, 5000);
  }

  private clearFeedback(): void {
    this.feedbackMessage = '';
    this.feedbackError = '';
  }

  // --- DOCUMENTOS EN LA NUBE Y CONSTANCIAS ---
  hasValidDocument(fileUrl: string | null | undefined): boolean {
    if (!fileUrl || fileUrl === 'pending') return false;
    const trimmed = fileUrl.trim();
    return (
      trimmed.length > 0 &&
      (trimmed.startsWith('http://') ||
        trimmed.startsWith('https://') ||
        trimmed.startsWith('/'))
    );
  }

  openDocument(fileUrl?: string | null): void {
    if (!fileUrl || fileUrl === 'pending') return;
    window.open(fileUrl, '_blank', 'noopener,noreferrer');
  }

  hasDocument(
    student: Student,
    field:
      | 'cartaAceptacion'
      | 'cartaTermino'
      | 'constanciaPlatica'
      | 'reporteActividades'
      | 'reporteFinal',
  ): boolean {
    return this.hasValidDocument(student?.[field]);
  }

  openAcceptanceCertificate(student: Student): void {
    const id = (student as any)?._id || student?.id || 'demo';
    const url =
      this.hasDocument(student, 'cartaAceptacion') &&
      student.cartaAceptacion.startsWith('http')
        ? student.cartaAceptacion
        : `${environment.apiUrl}/student/${id}/certificate/acceptance`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  openCompletionCertificate(student: Student): void {
    const id = (student as any)?._id || student?.id || 'demo';
    const url =
      this.hasDocument(student, 'cartaTermino') &&
      student.cartaTermino.startsWith('http')
        ? student.cartaTermino
        : `${environment.apiUrl}/student/${id}/certificate/completion`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  // --- ACTIVIDADES PRIORIZADAS (FASE 3) ---
  getActivityTitle(act: string): string {
    if (!act) return '';
    return act.replace(/\s*\((?:Acredita|Horas):\s*([^)]+)\)\s*$/i, '').trim();
  }

  getActivityHours(act: string): string | null {
    if (!act) return null;
    const match = act.match(/\((?:Acredita|Horas):\s*([^)]+)\)\s*$/i);
    return match ? match[1].trim() : null;
  }

  // --- REFLEXIONES FORMATIVAS (FASE 2) ---
  get reflectionsList() {
    const refs = this.student?.onboardingData?.reflections || {};
    const modules = [
      { id: 'modulo-1', name: 'Módulo 1: Historia, Misión y Visión de ABAQ' },
      { id: 'modulo-2', name: 'Módulo 2: Valores y Cultura del Voluntariado' },
      { id: 'modulo-3', name: 'Módulo 3: Bienestar y Rescate Animal' },
      { id: 'modulo-4', name: 'Módulo 4: Protocolos de Seguridad y Convivencia' },
      { id: 'modulo-5', name: 'Módulo 5: Adopciones Responsables y Campañas' },
      { id: 'modulo-6', name: 'Módulo 6: Organización de Eventos y Difusión' },
    ];
    return modules.map((m) => ({
      ...m,
      data: refs[m.id] || null,
    }));
  }

  hasAnyReflections(): boolean {
    const refs = this.student?.onboardingData?.reflections;
    return !!refs && Object.keys(refs).length > 0;
  }

  // --- DOCUMENTOS NORMATIVOS ---
  getLegalDocUrl(docKey: string): string {
    const doc = this.legalDocs[docKey];
    return doc?.url || '#';
  }

  openLegalDoc(docKey: string, event?: Event): void {
    const doc = this.legalDocs[docKey];
    const url = doc?.url;
    if (url && url.startsWith('http')) {
      return;
    }

    if (event) {
      event.preventDefault();
    }

    const win = window.open('', '_blank');
    if (win) {
      const docTitle = doc?.title || 'Documento Normativo Institucional';
      win.document.write(`
        <!DOCTYPE html>
        <html lang="es">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>${docTitle} · ABAQ</title>
          <style>
            body {
              font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              display: flex;
              align-items: center;
              justify-content: center;
              min-height: 100vh;
              margin: 0;
              background: #f8fafc;
              color: #1e293b;
              padding: 24px;
              box-sizing: border-box;
            }
            .card {
              max-width: 540px;
              width: 100%;
              padding: 40px 32px;
              background: #ffffff;
              border-radius: 20px;
              border: 1px solid #e2e8f0;
              box-shadow: 0 16px 36px rgba(36, 54, 111, 0.08);
              text-align: center;
            }
            .badge {
              display: inline-block;
              background: #ecfdf5;
              color: #065f46;
              border: 1px solid #a7f3d0;
              padding: 5px 14px;
              border-radius: 999px;
              font-size: 11.5px;
              font-weight: 800;
              letter-spacing: 0.5px;
              margin-bottom: 20px;
              text-transform: uppercase;
            }
            h1 {
              color: #24366f;
              margin: 0 0 12px;
              font-size: 22px;
              font-weight: 800;
            }
            p {
              color: #64748b;
              line-height: 1.6;
              font-size: 14.5px;
              margin: 0 0 24px;
            }
            .info-box {
              background: #f1f5f9;
              border-radius: 12px;
              padding: 16px;
              font-size: 13px;
              color: #334155;
              border: 1px solid #cbd5e1;
            }
          </style>
        </head>
        <body>
          <div class="card">
            <span class="badge">✓ Validado en Expediente Digital</span>
            <h1>${docTitle}</h1>
            <p>Este documento normativo fue revisado y aceptado expresamente durante el proceso de registro institucional en ABAQ Querétaro.</p>
            <div class="info-box">
              📄 La versión digital descargable para consulta pública se encuentra en preparación y estará vinculada a este enlace próximamente.
            </div>
          </div>
        </body>
        </html>
      `);
      win.document.close();
    }
  }

  // --- ETIQUETAS DE TIPO, ESTADO Y DESEMPEÑO ---
  getAccreditationTypeLabel(type?: string | null): string {
    switch (type) {
      case 'EVENT':
        return 'Evento ABAQ';
      case 'INDUCTION':
        return 'Inducción';
      case 'IDENTIFICATION':
        return 'Placas de identificación';
      case 'STERILIZATION':
        return 'Esterilización';
      case 'ADOPTION':
        return 'Adopción';
      case 'ADOPTION_MODULE':
        return 'Módulo de adopción';
      case 'TALK':
        return 'Plática ABAQ';
      case 'CAMPAIGN':
        return 'Campaña';
      case 'MEDIA':
        return 'Difusión en medios';
      case 'SOCIAL_MEDIA':
        return 'Redes sociales';
      case 'DONATION':
        return 'Recaudación de fondos';
      case 'ADMINISTRATIVE':
        return 'Actividad administrativa';
      default:
        return type || 'Acreditación institucional';
    }
  }

  getAccreditationStatusLabel(status?: string | null): string {
    switch (status) {
      case 'approved':
      case 'accredited':
        return 'Aprobada';
      case 'pending':
        return 'En revisión';
      case 'rejected':
        return 'Rechazada';
      default:
        return status || 'Registrada';
    }
  }

  getActivityStatusLabel(status?: string | null): string {
    switch (status) {
      case 'registered':
        return 'Registrado';
      case 'attended':
        return 'Asistió';
      case 'validated':
        return 'Horas validadas';
      case 'absent':
        return 'No asistió';
      case 'cancelled':
        return 'Cancelada';
      default:
        return status || 'Registrado';
    }
  }

  getPerformanceLabel(status?: string | null, performance?: string | null): string {
    const s = status || '';
    const p = performance || '';
    if (s === 'absent' || p === 'absent') return 'Inasistencia';
    const val = p && p !== 'pending' ? p : '';
    switch (val) {
      case 'excellent':
        return 'Excelente';
      case 'very_good':
        return 'Muy bueno';
      case 'good':
        return 'Bueno';
      case 'regular':
        return 'Regular';
      case 'bad':
        return 'Malo';
      case 'very_bad':
        return 'Muy malo';
      default:
        return 'Pendiente de evaluación';
    }
  }

  getPerformanceClass(status?: string | null, performance?: string | null): string {
    const s = status || '';
    const p = performance || '';
    if (s === 'absent' || p === 'absent') return 'perf-absent';
    const val = p && p !== 'pending' ? p : '';
    switch (val) {
      case 'excellent':
      case 'very_good':
        return 'perf-good';
      case 'good':
      case 'regular':
        return 'perf-neutral';
      case 'bad':
      case 'very_bad':
        return 'perf-bad';
      default:
        return 'perf-pending';
    }
  }

  get hasAnyFolios(): boolean {
    if (this.student?.folio) return true;
    const actFolios = (this.student?.assignedActivities || []).some(
      (a) => !!a.interactionFolio,
    );
    const accFolios = (this.accreditations || []).some((a) => !!a.interactionFolio);
    return actFolios || accFolios;
  }
}
